import React from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Home, Compass, MessageCircle, Search } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";

const COPY = {
  ca: {
    label: "Error 404",
    title: "Aquesta pàgina s'ha perdut al núvol.",
    subtitle: "L'enllaç que has seguit no existeix o ha canviat de lloc. No et preocupis, aquí sota tens els millors camins per continuar explorant HO-SYS.",
    requested: "Ruta sol·licitada",
    backHome: "Tornar a l'inici",
    talkNow: "Parla amb nosaltres",
    suggestionsTitle: "Potser buscaves això",
    suggestions: [
      { to: "/products", title: "Ecosistema HO-SYS", desc: "Les 8 plataformes intel·ligents que fem servir per digitalitzar empreses i ciutats." },
      { to: "/services", title: "Els nostres serveis", desc: "Software a mida, SaaS, IA, Smart Cities i digitalització d'extrem a extrem." },
      { to: "/projects", title: "Projectes reals", desc: "Casos i projectes ja en producció: ReusApp, TapaReus, Flaps, D'Gusta i més." },
      { to: "/blog", title: "Blog HO-SYS", desc: "Articles sobre IA, transformació digital, Smart Cities i escalar SaaS." },
      { to: "/contact", title: "Contacte", desc: "Explica'ns el teu projecte i et responem en 24 h feineres." },
      { to: "/case-studies", title: "Casos d'èxit", desc: "Resultats mesurables i testimonis dels nostres clients." },
    ],
  },
  es: {
    label: "Error 404",
    title: "Esta página se perdió en la nube.",
    subtitle: "El enlace que has seguido no existe o ha cambiado de sitio. No te preocupes, aquí abajo tienes los mejores caminos para seguir explorando HO-SYS.",
    requested: "Ruta solicitada",
    backHome: "Volver al inicio",
    talkNow: "Habla con nosotros",
    suggestionsTitle: "Quizás buscabas esto",
    suggestions: [
      { to: "/products", title: "Ecosistema HO-SYS", desc: "Las 8 plataformas inteligentes que usamos para digitalizar empresas y ciudades." },
      { to: "/services", title: "Nuestros servicios", desc: "Software a medida, SaaS, IA, Smart Cities y digitalización de extremo a extremo." },
      { to: "/projects", title: "Proyectos reales", desc: "Casos y proyectos en producción: ReusApp, TapaReus, Flaps, D'Gusta y más." },
      { to: "/blog", title: "Blog HO-SYS", desc: "Artículos sobre IA, transformación digital, Smart Cities y escalar SaaS." },
      { to: "/contact", title: "Contacto", desc: "Cuéntanos tu proyecto y te respondemos en 24 h laborables." },
      { to: "/case-studies", title: "Casos de éxito", desc: "Resultados medibles y testimonios de nuestros clientes." },
    ],
  },
  en: {
    label: "Error 404",
    title: "This page got lost in the cloud.",
    subtitle: "The link you followed doesn't exist or has moved. Don't worry — below are the best paths to keep exploring HO-SYS.",
    requested: "Requested path",
    backHome: "Back to home",
    talkNow: "Talk to us",
    suggestionsTitle: "You might be looking for",
    suggestions: [
      { to: "/products", title: "HO-SYS Ecosystem", desc: "The 8 intelligent platforms we use to digitize businesses and cities." },
      { to: "/services", title: "Our services", desc: "Custom software, SaaS, AI, Smart Cities and end-to-end digitalization." },
      { to: "/projects", title: "Real projects", desc: "Cases and products live in production: ReusApp, TapaReus, Flaps, D'Gusta and more." },
      { to: "/blog", title: "HO-SYS Blog", desc: "Articles on AI, digital transformation, Smart Cities and scaling SaaS." },
      { to: "/contact", title: "Contact", desc: "Tell us about your project and we'll get back within 24 business hours." },
      { to: "/case-studies", title: "Case studies", desc: "Measurable results and customer testimonials." },
    ],
  },
  fr: {
    label: "Erreur 404",
    title: "Cette page s'est perdue dans le nuage.",
    subtitle: "Le lien que vous avez suivi n'existe pas ou a changé. Pas d'inquiétude — voici les meilleurs chemins pour continuer à explorer HO-SYS.",
    requested: "Chemin demandé",
    backHome: "Retour à l'accueil",
    talkNow: "Parlez-nous",
    suggestionsTitle: "Vous cherchiez peut-être",
    suggestions: [
      { to: "/products", title: "Écosystème HO-SYS", desc: "Les 8 plateformes intelligentes qui numérisent entreprises et villes." },
      { to: "/services", title: "Nos services", desc: "Logiciel sur mesure, SaaS, IA, Smart Cities et numérisation de bout en bout." },
      { to: "/projects", title: "Projets réels", desc: "Cas et produits en production : ReusApp, TapaReus, Flaps, D'Gusta et plus." },
      { to: "/blog", title: "Blog HO-SYS", desc: "Articles sur l'IA, la transformation numérique, les Smart Cities et le SaaS." },
      { to: "/contact", title: "Contact", desc: "Parlez-nous de votre projet ; réponse en 24 h ouvrées." },
      { to: "/case-studies", title: "Études de cas", desc: "Résultats mesurables et témoignages clients." },
    ],
  },
};

export default function NotFound() {
  const { lang } = useI18n();
  const location = useLocation();
  const c = COPY[lang] || COPY.ca;
  useSEO({ lang, title: `${c.label} · HO-SYS`, description: c.subtitle, path: location.pathname, noindex: true });

  return (
    <div className="pt-32 pb-24 relative overflow-hidden" data-testid="not-found-page">
      {/* Ambient grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#2F7BFF 1px,transparent 1px),linear-gradient(90deg,#2F7BFF 1px,transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at top, black 40%, transparent 75%)",
        }}
      />

      <section className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-[#2F7BFF]">
            <span className="inline-block w-8 h-px bg-[#2F7BFF]" />
            {c.label}
          </div>
        </Reveal>

        <div className="mt-6 grid lg:grid-cols-[1.1fr_.9fr] gap-10 items-start">
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-extrabold tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.98]"
            >
              {c.title}
            </motion.h1>
            <Reveal delay={0.15}>
              <p className="mt-6 text-white/60 text-lg max-w-xl leading-relaxed">{c.subtitle}</p>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 font-mono text-xs text-white/50">
                <Search size={12} className="text-[#2F7BFF]" />
                <span className="text-white/40">{c.requested}:</span>
                <span className="text-white/80 truncate max-w-[280px] sm:max-w-md">{location.pathname}</span>
              </div>
            </Reveal>

            <Reveal delay={0.35}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to={`/${lang}`}
                  data-testid="not-found-home-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#2F7BFF] px-6 py-3 text-sm font-semibold text-white hover:bg-[#4C8DFF] transition-colors"
                >
                  <Home size={16} />
                  {c.backHome}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to={`/${lang}/contact`}
                  data-testid="not-found-contact-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3 text-sm font-semibold text-white hover:bg-white/[0.07] transition-colors"
                >
                  <MessageCircle size={16} />
                  {c.talkNow}
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Big 404 glyph */}
          <Reveal delay={0.1}>
            <div className="relative hidden lg:block">
              <div
                className="font-display font-black text-transparent bg-clip-text leading-none select-none"
                style={{
                  fontSize: "clamp(180px, 22vw, 340px)",
                  backgroundImage:
                    "linear-gradient(135deg,#2F7BFF 0%,#AEB9C7 55%,#0A0A0A 100%)",
                  WebkitTextStroke: "1px rgba(47,123,255,0.35)",
                  lineHeight: 0.85,
                }}
              >
                404
              </div>
              <motion.div
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.4 }}
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 50% at 60% 40%, rgba(47,123,255,0.25), transparent 70%)",
                }}
              />
            </div>
          </Reveal>
        </div>

        {/* Suggestions */}
        <div className="mt-24">
          <Reveal>
            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-white/50">
              <Compass size={12} />
              {c.suggestionsTitle}
            </div>
          </Reveal>

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.suggestions.map((s, i) => (
              <Reveal key={s.to} delay={0.05 * i}>
                <Link
                  to={`/${lang}${s.to}`}
                  data-testid={`not-found-suggest-${s.to.replace("/", "")}`}
                  className="group block h-full rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-[#2F7BFF]/50 hover:bg-white/[0.04] transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-lg font-semibold text-white group-hover:text-[#2F7BFF] transition-colors">
                      {s.title}
                    </h3>
                    <ArrowRight
                      size={16}
                      className="text-white/40 group-hover:text-[#2F7BFF] transition-transform group-hover:translate-x-1 mt-1"
                    />
                  </div>
                  <p className="mt-2 text-white/55 text-sm leading-relaxed">{s.desc}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
